import { useMemo } from 'react';
import { useAuthMe } from './authMe';

// =============================================================================
// useWriteGate — 쓰기 컨트롤의 활성/비활성을 한 곳에서 계산한다 (2026-09-16 검수 P1).
//
// 원칙(2026-09-16 보완): 두 종류를 구분한다.
//   · 일시적 차단(대행 읽기 전용, 꽉 찬 그룹 등) — 같은 자리에 두고 `disabled` + `title`(이유).
//   · 역할상 영구히 불가한 조작 — 아예 숨긴다(`and(..., { hide: true })` → `hidden`).
//     눌러 볼 수도 없는 버튼을 늘어놓으면 화면만 복잡해진다는 사용자 결정.
//
//   const write = useWriteGate();
//   <Button {...write.props}>삭제</Button>                        // 대행 읽기 전용만 판정
//   <Button {...write.and(isOwner, '조직 관리자만 가능').props}>  // 로컬 조건을 덧붙임
//
// 지금 판정하는 전역 조건은 BO 대행 모드의 읽기 전용 하나다(서버도 403으로 막는다).
// 로컬 역할 조건은 각 화면이 `and()`로 얹는다.
// =============================================================================

export const ACTING_READ_ONLY_REASON =
  '대행 모드(읽기 전용) — 상단 띠의 「편집 허용」을 켜세요';

export type WriteGate = {
  /** 지금 쓰기가 막혀 있다 → 버튼은 숨기지 말고 비활성. */
  blocked: boolean;
  /** 막힌 이유(툴팁). 막히지 않았으면 undefined. */
  reason: string | undefined;
  /** 역할상 영구히 불가 — 이 컨트롤은 렌더하지 않는다. */
  hidden: boolean;
  /** 로컬 조건 추가. 먼저 걸린 이유가 이긴다(대행 읽기 전용 우선). hide는 어느 단계에서 걸려도 유지된다. */
  and: (allowed: boolean, reason: string, opts?: { hide?: boolean }) => WriteGate;
  /** `disabled` + `title`을 한 번에. 버튼에 자체 title이 있으면 대신 reason ?? 자체 title. */
  props: { disabled: boolean; title: string | undefined };
};

function gate(blocked: boolean, reason: string | undefined, hidden = false): WriteGate {
  return {
    blocked,
    hidden,
    reason: blocked ? reason : undefined,
    and: (allowed, localReason, opts) => {
      const hide = hidden || (!allowed && !!opts?.hide);
      return blocked ? gate(true, reason, hide) : gate(!allowed, localReason, hide);
    },
    props: { disabled: blocked, title: blocked ? reason : undefined },
  };
}

/** 로컬 조건만으로 게이트를 만든다(훅 밖 · 목록 행 안에서 쓰는 헬퍼). */
export function writeGate(allowed: boolean, reason: string, opts?: { hide?: boolean }): WriteGate {
  return gate(!allowed, reason, !allowed && !!opts?.hide);
}

export function useWriteGate(): WriteGate {
  const { me } = useAuthMe();
  const readOnlyActing = me?.acting?.scope === 'read';
  return useMemo(
    () => gate(readOnlyActing, ACTING_READ_ONLY_REASON),
    [readOnlyActing],
  );
}
